const DEFAULT_KEY_MATERIAL =
  process.env.NEXT_PUBLIC_ENCRYPTION_KEY ||
  process.env.ENCRYPTION_KEY ||
  'totalaccess-default-key-change-in-production';

function safeStringify(value: unknown): string {
  const seen = new WeakSet();

  return JSON.stringify(value, (_key, currentValue) => {
    if (typeof currentValue === 'function') {
      return undefined;
    }

    if (currentValue instanceof Date) {
      return currentValue.toISOString();
    }

    if (typeof currentValue === 'object' && currentValue !== null) {
      if (seen.has(currentValue)) {
        return '[Circular]';
      }
      seen.add(currentValue);
    }

    return currentValue;
  });
}

function getCrypto(): Crypto {
  if (typeof globalThis.crypto === 'undefined' || !globalThis.crypto?.subtle) {
    throw new Error('Web Crypto API is unavailable');
  }

  return globalThis.crypto;
}

function bytesToBase64(bytes: Uint8Array): string {
  if (typeof btoa === 'function') {
    let binary = '';
    for (const byte of bytes) {
      binary += String.fromCharCode(byte);
    }
    return btoa(binary);
  }

  return Buffer.from(bytes).toString('base64');
}

function base64ToBytes(value: string): Uint8Array {
  if (typeof atob === 'function') {
    const binary = atob(value);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  return new Uint8Array(Buffer.from(value, 'base64'));
}

class OfflinePayloadCrypto {
  private readonly keyMaterial: string;
  private keyPromise: Promise<CryptoKey> | null = null;

  constructor(keyMaterial: string = DEFAULT_KEY_MATERIAL) {
    this.keyMaterial = keyMaterial;
  }

  private async getKey(): Promise<CryptoKey> {
    if (this.keyPromise) {
      return this.keyPromise;
    }

    this.keyPromise = (async () => {
      const crypto = getCrypto();
      const encoder = new TextEncoder();
      const baseKey = await crypto.subtle.importKey(
        'raw',
        encoder.encode(this.keyMaterial),
        'PBKDF2',
        false,
        ['deriveBits', 'deriveKey'],
      );

      return crypto.subtle.deriveKey(
        {
          name: 'PBKDF2',
          salt: encoder.encode('totalaccess-offline-db-salt'),
          iterations: 100000,
          hash: 'SHA-256',
        },
        baseKey,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt'],
      );
    })();

    return this.keyPromise;
  }

  async encryptValue<T>(value: T): Promise<string> {
    const crypto = getCrypto();
    const key = await this.getKey();
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const plaintext = new TextEncoder().encode(safeStringify(value));

    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      plaintext,
    );

    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encrypted), iv.length);

    return bytesToBase64(combined);
  }

  async decryptValue<T>(ciphertext: string): Promise<T> {
    try {
      const crypto = getCrypto();
      const key = await this.getKey();
      const combined = base64ToBytes(ciphertext);

      if (combined.length < 28) {
        throw new Error('Invalid ciphertext payload');
      }

      const iv = combined.slice(0, 12);
      const encrypted = combined.slice(12);
      const decrypted = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv },
        key,
        encrypted,
      );

      return JSON.parse(new TextDecoder().decode(decrypted)) as T;
    } catch (error) {
      throw new Error('OFFLINE_DECRYPTION_FAILED');
    }
  }
}

const offlinePayloadCrypto = new OfflinePayloadCrypto();

export default offlinePayloadCrypto;
export { OfflinePayloadCrypto, safeStringify };